import { z } from 'zod';
import type { Status } from '@pillar-portal/database/dist/frontend';
import type { OpaqueCursorPagination } from './authorization.types';
import type { IpgSystemId } from './types/third-party-system.enums';
import type { BusinessDetails, ProcessorConfig } from './backend_config.types';
import type {
    OpaqueCursorSearchRequest,
} from './omnisearch.types';

export interface MerchantAccountConfiguration {
    allow_surcharge: boolean;
    allow_convenience_fee: boolean;
    allow_override_fee: boolean;
    addressRequiredForCardPayment: boolean;
    surcharge_pct: number; // Default: 0, minimum:0, maximum:100, scale: percentage
    convenience_fee: number; // Default: 0, minimum:0, maximum:unspecified, scale: dollars
}

export const MerchantAccountConfigurationInputSchema: z.ZodObject<{
    allow_surcharge: z.ZodDefault<z.ZodBoolean>;
    allow_convenience_fee: z.ZodDefault<z.ZodBoolean>;
    allow_override_fee: z.ZodDefault<z.ZodBoolean>;
    addressRequiredForCardPayment: z.ZodOptional<z.ZodBoolean>;
    allow_missing_billing_address: z.ZodOptional<z.ZodBoolean>;
    surcharge_pct: z.ZodDefault<z.ZodNumber>;
    convenience_fee: z.ZodDefault<z.ZodNumber>;
}> = z.object({
    allow_surcharge: z.boolean().default(false),
    allow_convenience_fee: z.boolean().default(false),
    allow_override_fee: z.boolean().default(false),
    addressRequiredForCardPayment: z.boolean().optional(),
    allow_missing_billing_address: z.boolean().optional(),
    surcharge_pct: z.number().min(0).max(100).default(0),
    convenience_fee: z.number().min(0).default(0),
});

export const resolveMerchantAccountConfiguration = (
    value: z.input<typeof MerchantAccountConfigurationInputSchema>,
    defaultAddressRequiredForCardPayment: boolean = true
): MerchantAccountConfiguration => ({
    allow_surcharge: value.allow_surcharge ?? false,
    allow_convenience_fee: value.allow_convenience_fee ?? false,
    allow_override_fee: value.allow_override_fee ?? false,
    addressRequiredForCardPayment:
        value.addressRequiredForCardPayment ??
        (value.allow_missing_billing_address !== undefined
            ? !value.allow_missing_billing_address
            : defaultAddressRequiredForCardPayment),
    surcharge_pct: value.surcharge_pct ?? 0,
    convenience_fee: value.convenience_fee ?? 0,
});

export const parseMerchantAccountConfiguration = (
    value: unknown,
    defaultAddressRequiredForCardPayment: boolean = true
): MerchantAccountConfiguration =>
    resolveMerchantAccountConfiguration(
        MerchantAccountConfigurationInputSchema.parse(value),
        defaultAddressRequiredForCardPayment
    );

export const MerchantAccountConfigurationSchema: z.ZodType<MerchantAccountConfiguration> =
    MerchantAccountConfigurationInputSchema.transform((value) =>
        resolveMerchantAccountConfiguration(value)
    );

// Logical terminal: One or more terminals feeding transactions into a collective batch
export interface LogicalTerminalConfiguration {
    allow_override_fee?: boolean;
    surcharge_pct?: number;
    convenience_fee?: number;
}

// Terminal device: Either a physical card reader or a gateway endpoint (aka "virtual terminal")
export interface TerminalDeviceConfiguration {
    allow_override_fee?: boolean;
    surcharge_pct?: number;
    convenience_fee?: number;
}

export interface ProcessorListItem {
    processor_uuid: string;
    processor_name: string;
    status: Status;
}

export interface ProcessorSearchPageRequest {
    q?: string;
    limit?: number;
    cursor?: string;
}

export interface ProcessorSearchRequest extends OpaqueCursorSearchRequest {
    organization_uuid?: string;
    show_deleted?: boolean;
}

export interface ProcessorSearchPageResponse {
    processors: ProcessorListItem[];
    pagination: OpaqueCursorPagination;
}

export interface CreateProcessorRequest {
    processor_name: string;
    processor_config: ProcessorConfig;
}

export interface GetProcessorResponse extends ProcessorListItem {
    processor_config: ProcessorConfig;
}

export interface UpdateProcessorRequest {
    processor_name?: string;
    processor_config?: ProcessorConfig;
    status?: Status;
}

export interface MerchantAccountSearchListItem {
    merchant_account_uuid: string;
    processor_name: string;
    processor_mid: string;
    organization_uuid: string;
    organization_name: string;
    businessDetails?: BusinessDetails;
    applies_to_children: boolean;
    status: Status;
    allow_surcharge: boolean;
    surcharge_pct: number;
    duplicate_processor_mid_count: number;
    has_duplicate_processor_mid: boolean;
}

export interface MerchantAccountSearchPageRequest {
    q?: string;
    limit?: number;
    cursor?: string;
    duplicates_only?: boolean;
    merchant_organization_uuid?: string;
}

export interface MerchantAccountSearchRequest extends OpaqueCursorSearchRequest {
    duplicates_only?: boolean;
    show_deleted?: boolean;
    merchant_organization_uuid?: string;
    organization_uuid?: string;
    include_descendants?: boolean;
}

export interface MerchantAccountSearchPageResponse {
    merchant_accounts: MerchantAccountSearchListItem[];
    pagination: OpaqueCursorPagination;
    duplicate_summary: {
        has_duplicates: boolean;
        duplicate_records_count: number;
    };
}

export interface MerchantAccountListItem extends MerchantAccountSearchListItem {
    merchant_account_id: number;
    organization_id: number;
}

export interface CreateMerchantAccountRequest {
    processor_name: string;
    processor_mid: string;
    organization_uuid: string;
    merchant_account_configuration: MerchantAccountConfiguration;
}

export interface UpdateMerchantAccountRequest {
    processor_name?: string;
    processor_mid?: string;
    applies_to_children?: boolean;
    status?: Status;
    merchant_account_configuration?: MerchantAccountConfiguration;
}

export interface GetMerchantAccountResponse extends MerchantAccountSearchListItem {
    merchant_account_configuration: MerchantAccountConfiguration;
}

export interface LogicalTerminalListItem {
    logical_terminal_uuid: string;
    merchant_account_uuid: string;
    name: string | null;
    ipg_system_id: IpgSystemId;
    processor_name: string;
    processor_mid: string;
    processor_tid: string;
    businessDetails?: BusinessDetails;
    applies_to_children: boolean | null;
    status: Status;
    duplicate_processor_tid_count: number;
    has_duplicate_processor_tid: boolean;
}

export interface LogicalTerminalSearchPageRequest {
    q?: string;
    limit?: number;
    cursor?: string;
    duplicates_only?: boolean;
    merchant_account_uuid?: string;
}

export interface LogicalTerminalSearchRequest extends OpaqueCursorSearchRequest {
    duplicates_only?: boolean;
    show_deleted?: boolean;
    merchant_account_uuid?: string;
    organization_uuid?: string;
}

export interface LogicalTerminalSearchPageResponse {
    logical_terminals: LogicalTerminalListItem[];
    pagination: OpaqueCursorPagination;
    duplicate_summary: {
        has_duplicates: boolean;
        duplicate_records_count: number;
    };
}

export interface MerchantOrganizationMerchantAccountSummaryItem {
    merchant_account_uuid: string;
    processor_name: string;
    processor_mid: string;
    status: Status;
    allow_surcharge: boolean;
    surcharge_pct: number;
}

export interface MerchantOrganizationListItem {
    organization_uuid: string;
    name: string;
    businessDetails?: BusinessDetails;
    merchant_accounts: MerchantOrganizationMerchantAccountSummaryItem[];
}

export interface MerchantOrganizationSearchPageRequest {
    root_organization_uuid: string;
    q?: string;
    limit?: number;
    cursor?: string;
}

export interface MerchantOrganizationSearchRequest extends OpaqueCursorSearchRequest {
    root_organization_uuid: string;
}

export interface MerchantOrganizationSearchPageResponse {
    merchant_organizations: MerchantOrganizationListItem[];
    pagination: OpaqueCursorPagination;
}

export interface PreviewLogicalTerminalOrganizationChangeRequest {
    organization_uuid?: string | null;
}

export interface LogicalTerminalOrganizationConflictItem {
    terminal_device_uuid: string;
    processor_device_id: string;
    organization_uuid: string;
    organization_name: string;
}

export interface PreviewLogicalTerminalOrganizationChangeResponse {
    conflicts: LogicalTerminalOrganizationConflictItem[];
}

export interface CreateLogicalTerminalRequest {
    merchant_account_uuid: string;
    ipg_system_id: IpgSystemId;
    name?: string | null;
    processor_tid: string;
    organization_uuid?: string | null;
    logical_terminal_configuration?: LogicalTerminalConfiguration;
    create_default_physical_terminal_device?: boolean;
    create_default_virtual_terminal_device?: boolean;
}

export interface UpdateLogicalTerminalRequest {
    name?: string | null;
    processor_tid?: string;
    organization_uuid?: string | null;
    applies_to_children?: boolean | null;
    clear_conflicting_terminal_device_organization_ids?: boolean;
    status?: Status;
    logical_terminal_configuration?: LogicalTerminalConfiguration;
    credential_uuid?: string | null;
}

export interface GetLogicalTerminalResponse extends LogicalTerminalListItem {
    organization_uuid: string | null;
    logical_terminal_configuration: LogicalTerminalConfiguration;
    credential_uuid: string | null;
}

export interface TerminalDeviceListItem {
    terminal_device_uuid: string;
    logical_terminal_uuid: string;
    name: string | null;
    processor_name: string;
    processor_mid: string;
    processor_tid: string;
    processor_device_id: string;
    businessDetails?: BusinessDetails;
    is_virtual: boolean;
    status: Status;
    duplicate_processor_device_id_count: number;
    has_duplicate_processor_device_id: boolean;
}

export interface TerminalDeviceSearchPageRequest {
    q?: string;
    limit?: number;
    cursor?: string;
    duplicates_only?: boolean;
    logical_terminal_uuid?: string;
}

export interface TerminalDeviceSearchRequest extends OpaqueCursorSearchRequest {
    duplicates_only?: boolean;
    show_deleted?: boolean;
    logical_terminal_uuid?: string;
    organization_uuid?: string;
}

export interface TerminalDeviceSearchPageResponse {
    terminal_devices: TerminalDeviceListItem[];
    pagination: OpaqueCursorPagination;
    duplicate_summary: {
        has_duplicates: boolean;
        duplicate_records_count: number;
    };
}

export interface CreateTerminalDeviceRequest {
    logical_terminal_uuid: string;
    processor_device_id: string;
    is_virtual: boolean;
    organization_uuid?: string | null;
    terminal_device_configuration?: TerminalDeviceConfiguration;
}

export interface UpdateTerminalDeviceRequest {
    name?: string | null;
    processor_device_id?: string;
    is_virtual?: boolean;
    organization_uuid?: string | null;
    status?: Status;
    terminal_device_configuration?: TerminalDeviceConfiguration;
    credential_uuid?: string | null;
}

export interface GetTerminalDeviceResponse extends TerminalDeviceListItem {
    organization_uuid: string | null;
    terminal_device_configuration: TerminalDeviceConfiguration;
    credential_uuid: string | null;
}
